import { FormattedEvent } from '@eventbrite/event-renderer';
import { usePlacementEventContextAggregator } from '@eventbrite/event-tracker';
import {
    trackAnalyticsEvent,
    trackEventFromState,
} from '@eventbrite/site-analytics';
import { $FixMe } from '@eventbrite/ts-utils';
import loadable from '@loadable/component';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import React, {
    FocusEventHandler,
    FunctionComponent,
    MouseEventHandler,
    useContext,
    useEffect,
    useRef,
} from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';

import { GA_SEARCH_CATEGORY } from '../../../../constants/analytics';
import { trackEventClickInternal as trackEventClickInternalAction } from '../../../../redux/actions/analytics';
import { setHoverEventTarget as setHoverEventTargetAction } from '../../../../redux/actions/ui';
import { TrackingContext } from '../../../../trackingContext';
import { FeatureFlags, PaginationData } from '../../../../types';

import { useServerEnrolledStatsigExperiment } from '../../../../context/experiments';
import { useInitialEventsExpansionQueryState } from '../../SearchPageDataExpansions';
import { addSearchIdToGaAction } from '../../utils/analytics';
import { aggregateTrackingContext } from '../../utils/tracking';
import './SearchResultPanelContent.scss';
import { SearchResultPanelContentEventCard } from './SearchResultPanelContentEventCard';
import { SEARCH_TOP_PROMOTED_VERTICAL_CARDS } from './SearchResultPanelContentExperiments';

const LoadingCards = loadable(
    () => import('../../../../components/LoadingCards'),
);

type SearchResultsProps = {
    events: FormattedEvent[];
    isLoading: boolean;
    pagination: PaginationData;
};

type SearchResultsReduxProps = {
    history?: $FixMe;
    onMouseOverCard?: Function;
    onMouseOutCard?: MouseEventHandler<HTMLDivElement> &
        FocusEventHandler<HTMLDivElement>;
    isAuthenticated?: boolean;
    isOnline?: boolean;
    isMobile?: boolean;
    featureFlags?: FeatureFlags;
    shouldShowDebugOutput?: boolean;
    setHoverEventTarget: Function;
    userId: string;
    searchId: string;
    eventSearch: $FixMe;
    focusedEvents: string[];
    raisedEvents: string[];
    sessionId: string;
    guestId: string;
    // Tracking functions
    trackGAEvent: Function;
    onTrackEventClickInternal: Function;
    locale: string;
};

export const SearchResultPanelContentInternal = ({
    events = [],
    eventSearch,
    searchId,
    userId,
    sessionId,
    guestId,
    isLoading,
    pagination,
    isAuthenticated = false,
    trackGAEvent,
    onTrackEventClickInternal,
    shouldShowDebugOutput,
    setHoverEventTarget,
    focusedEvents,
    raisedEvents,
    locale,
}: SearchResultsProps & SearchResultsReduxProps) => {
    const placementDomElement = useRef(null);
    const { locale: trackingLocale } = useContext(TrackingContext);
    const hasEventResults = !isEmpty(events);

    const expansionStatus = useInitialEventsExpansionQueryState();

    useSectionTrackingEffects(events, eventSearch.q);
    usePlacementEventContextAggregator(placementDomElement, ({ id, event }) =>
        aggregateTrackingContext({
            id,
            event,
            events,
            eventSearch,
            searchId,
            userId,
            currentPage: pagination.page,
            locale: trackingLocale,
            sessionId,
            guestId,
        }),
    );

    const { results, topSearchEvents } = useCategorizedEvents(events);

    if (!isLoading && !hasEventResults) {
        return null;
    }

    if (isLoading) {
        return (
            <section ref={placementDomElement}>
                <div className="search-result-loading__desktop">
                    <LoadingCards isFlipped={false} style={'list-responsive'} />
                </div>
                <div className="search-result-loading__mobile">
                    <LoadingCards isFlipped={false} style="grid" />
                </div>
            </section>
        );
    }

    const listItemClassName = classNames(
        'search-main-content__events-list-item',
        'search-main-content__events-list-item__mobile',
    );

    const commonCardProps = {
        isAuthenticated,
        trackGAEvent,
        onTrackEventClickInternal,
        locale,
        shouldShowDebugOutput,
        setHoverEventTarget,
        scrollId: focusedEvents[0],
        isExpansionsLoading:
            expansionStatus === undefined || expansionStatus === 'loading',
    };

    return (
        <section ref={placementDomElement}>
            {!isEmpty(topSearchEvents) && (
                <ul className="search-main-content__promoted-events">
                    {topSearchEvents.map((event: any, index: number) => (
                        <li
                            className={listItemClassName}
                            key={`${event.id}-${event.isPromoted}`}
                        >
                            <SearchResultPanelContentEventCard
                                {...commonCardProps}
                                position={index + 1}
                                isRaised={
                                    focusedEvents.includes(event.id) ||
                                    raisedEvents.includes(event.id)
                                }
                                event={event}
                                forceVariant="mobile"
                            />
                        </li>
                    ))}
                </ul>
            )}
            <ul className="search-main-content__events-list">
                {results.map((event: any, index: number) => (
                    <li
                        className={listItemClassName}
                        key={`${event.id}-${event.isPromoted}`}
                    >
                        <SearchResultPanelContentEventCard
                            {...commonCardProps}
                            position={index + 1}
                            isLazyImage={index > 3}
                            isRaised={
                                focusedEvents.includes(event.id) ||
                                raisedEvents.includes(event.id)
                            }
                            event={event}
                        />
                    </li>
                ))}
            </ul>
        </section>
    );
};

function useCategorizedEvents(events: FormattedEvent[]) {
    const { variant: areTopPromotedResultsEnabled } =
        useServerEnrolledStatsigExperiment(SEARCH_TOP_PROMOTED_VERTICAL_CARDS);

    if (areTopPromotedResultsEnabled) {
        return {
            results: events.filter(({ isPromoted }) => !isPromoted),
            topSearchEvents: events.filter(({ isPromoted }) => isPromoted),
        };
    } else {
        return {
            results: events,
            topSearchEvents: [],
        };
    }
}

const useSectionTrackingEffects = (
    content: FormattedEvent[],
    query: string,
) => {
    useEffect(() => {
        const hasDedupedEvent = content.some(hasDedup);

        if (hasDedupedEvent) {
            trackAnalyticsEvent({
                category: 'search',
                action: 'DeDupedResultsShown',
            });
        }
    });

    useEffect(() => {
        const hasPromotedEvents = content.some(({ isPromoted }) => isPromoted);
        if (!hasPromotedEvents) {
            return;
        }

        trackAnalyticsEvent({
            category: GA_SEARCH_CATEGORY,
            action: 'PromotedResultShown',
            label: query,
            nonInteraction: true,
        });
    }, [content, query]);
};

function hasDedup({ dedup }: FormattedEvent) {
    return dedup && typeof dedup.count === 'number' && dedup.count > 1;
}

export const _mapStateToProps = (state: any, ownProps: SearchResultsProps) => {
    return {
        eventSearch: state.search.eventSearch,
        isAuthenticated: state.auth.isAuthenticated,
        featureFlags: state.app.featureFlags,
        shouldShowDebugOutput: state.admin.shouldShowDebugOutput,
        userId: state.app.publicId,
        searchId: state.search.searchId,
        focusedEvents: state.ui.focusedEventCards,
        raisedEvents: state.ui.raisedEventCards,
        sessionId: state.app.sessionId,
        guestId: state.app.guestId,
        locale: state.app.locale,
        trackGAEvent: (action: any) =>
            trackEventFromState(state, addSearchIdToGaAction(action, state)),
        ...ownProps,
    };
};

const _mapDispatchToProps = (dispatch: Function) => ({
    setHoverEventTarget: (...args: [$FixMe]) =>
        dispatch(setHoverEventTargetAction(...args)),
    onTrackEventClickInternal: (...args: [$FixMe]) =>
        dispatch(trackEventClickInternalAction(...args)),
});

export const SearchResultPanelContent: FunctionComponent<SearchResultsProps> =
    compose<$FixMe>(
        withRouter,
        connect(_mapStateToProps, _mapDispatchToProps),
    )(SearchResultPanelContentInternal);
